import {
  type InfiniteData,
  type QueryFunctionContext,
  type UseInfiniteQueryResult,
  useInfiniteQuery,
} from '@tanstack/react-query';

import { teamService } from '@/services/team-service';
import type { TeamRole } from '@/types/core/team';
import type { Page } from '@/types/shared/page';
import { isEmptyUUID, type UUID } from '@/utils/uuid';

import { teamQueryKeys } from './query-keys';

/**
 * Fetch roles assigned to a team.
 * Accepts optional paging args via `opts`.
 */
export function useFetchTeamRoles(
  tenantId: UUID | undefined,
  teamId: UUID | undefined,
  opts?: {
    limit?: number;
    next?: string;
    filter?: string;
    enabled?: boolean;
  }
): UseInfiniteQueryResult<InfiniteData<Page<TeamRole>, unknown>, Error> {
  return useInfiniteQuery<
    Page<TeamRole>,
    Error,
    InfiniteData<Page<TeamRole>, unknown>
  >({
    queryKey: teamQueryKeys.roleList(
      tenantId,
      teamId,
      opts?.limit,
      opts?.filter
    ),
    queryFn: (context: QueryFunctionContext) => {
      if (isEmptyUUID(tenantId) || isEmptyUUID(teamId)) {
        throw new Error('Missing tenantId or teamId');
      }
      const pageParam = context.pageParam as string | undefined;
      return teamService.listTeamRoles(tenantId!, teamId!, {
        limit: opts?.limit,
        next: pageParam,
        filter: opts?.filter,
      });
    },
    enabled: opts?.enabled ?? (!isEmptyUUID(tenantId) && !isEmptyUUID(teamId)),
    initialPageParam: opts?.next ?? undefined,
    getNextPageParam: (last) => last.next ?? undefined,
    getPreviousPageParam: (first) => first.prev ?? undefined,
  });
}
